import fetchJsonp from 'fetch-jsonp';
import { FormikActions } from 'formik';
import React from 'react';
import Media from 'react-media';
import { connect } from 'react-redux';
import styled from 'styled-components';

import ContactForm from 'components/ContactForm';
import CompactResultTable from 'components/results/CompactResultTable';
import ResultTable from 'components/results/ResultTable';
import TechDataHeader from 'components/results/TechDataHeader';
import TechDataTable from 'components/results/TechDataTable';
import { BackButton, NextButton } from './components/step/NavigationButton';
import Step from './components/step/Step';
import Title from './components/Title';
import ApplicationCardContainer from './components/ui-components/ApplicationCardContainer';
import { IApplication, IProduct } from './models';
import {
  handleNextClick,
  handlePreviousClick,
  handleResetClick,
  handleResultSelectedClick,
  setApplication,
  setInput
} from './redux/actions/appActions';
import { IApplicationState } from './redux/reducers/rootReducer';
import { getApplications, getFilteredProducts, getProducts } from './redux/selectors';

import './App.css';

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 26px;
`;

const ResultsContainer = styled.div`
  text-align: center;
  margin: 16px;
`;

interface IProps {
  applications: IApplication[];
  techData: any;
  products: IProduct[];
  filteredProducts: IProduct[];
  application: IApplication | null;
  inputs: Map<string, string>;
  setApplication: (application: IApplication | null) => void;
  setInput: (inputs: Map<string, string>) => void;
  handlePreviousClick: () => void;
  handleNextClick: (filters: Map<string, string>) => void;
  handleResetClick: () => void;
  handleResultSelectedClick: (productToBeSelected: IProduct) => void;
  selection: Map<string, string> | null;
  selectedProduct: IProduct | null;
  productToBeSelected: IProduct | null;
  showResults: boolean;
}

interface IState {
  selectedApplication: IApplication | null;
  makeContactRequest: boolean;
  selectedProduct: IProduct | null;
  finished: boolean;
  error: string | null;
  formInput: {
    email: string,
    firstname: string,
    lastname: string,
    company: string,
    phone: string,
    country: string,
    marketsegment: string,
    mbquote: string
  };
}

class App extends React.Component<IProps, IState> {
  state = {
    selectedApplication: null,
    makeContactRequest: false,
    selectedProduct: null,
    finished: false,
    error: null,
    formInput: {
      email: '',
      firstname: '',
      lastname: '',
      company: '',
      phone: '',
      country: '',
      marketsegment: '',
      mbquote: ''
    }
  };

  handleApplicationClicked = (selectedApplication: IApplication) => {
    this.setState({
      selectedApplication
    });
  }

  handleNextClicked = () => {
    this.props.setApplication(this.state.selectedApplication);
  }

  handleResetClicked = () => {
    this.setState({
      selectedApplication: null,
      makeContactRequest: false,
      selectedProduct: null,
      finished: false,
      error: null,
      formInput: {
        email: '',
        firstname: '',
        lastname: '',
        company: '',
        phone: '',
        country: '',
        marketsegment: '',
        mbquote: ''
      }
    });
    this.props.handleResetClick();
  }

  handleFiltersChanged = (filters: Map<string, string>) => {
    this.props.setInput(filters);
  }

  isNextDisabled = () => {
    return this.state.selectedApplication === null;
  }

  setFinished = (finished: boolean) => {
    this.setState({ finished });
  }

  setError = (error: string) => {
    this.setState({ error });
  }

  renderResults = () => {
    const {
      filteredProducts,
      handlePreviousClick,
      handleNextClick
    } = this.props;

    const handleProductSelected = (selectedProduct: IProduct) => {
      this.props.handleResultSelectedClick(selectedProduct);
      this.setState({ selectedProduct });
    };

    if (filteredProducts != null) {
      return (
        <ResultsContainer>
          <Title title="Results" />
          <Media query="(min-width: 640px)">
            {(matches) =>
              matches && filteredProducts.length > 1
                ? (
                  <ResultTable
                    products={filteredProducts}
                    productSelected={handleProductSelected}
                  />
                )
                : (
                  <CompactResultTable
                    products={filteredProducts}
                    productSelected={handleProductSelected}
                  />
                )
            }
          </Media>
          <NavigationContainer>
            <BackButton
              type="button"
              hidden={false}
              onClick={() => handlePreviousClick()}
              disabled={false}
            >
              Back
            </BackButton>
            <NextButton
              type="button"
              hidden={false}
              onClick={() => handleNextClick(new Map<string, string>())}
              disabled={this.state.selectedProduct === null}
            >
              Next
            </NextButton>
          </NavigationContainer>
        </ResultsContainer >
      );
    }
    return <div>No results.</div>;
  }

  renderApplicationSelection = (applications: IApplication[] | null) => {
    if (applications === null) return <div />;
    return (
      <div>
        <Title title="APPLICATION" subtitle="Start by selecting the engine application" />
        <ApplicationCardContainer
          onApplicationClick={this.handleApplicationClicked}
          applications={applications}
        />
        <NavigationContainer>
          <NextButton
            type="button"
            onClick={this.handleNextClicked}
            disabled={this.isNextDisabled()}
          >
            Next
          </NextButton>
        </NavigationContainer>
      </div>
    );
  }

  renderTechDataPage = () => {
    const Container = styled.div`
      display: flex;
      flex-flow: column;
      padding: 48px;
    `;

    return (
      <Container>
        <TechDataHeader
          selectedProduct={this.props.selectedProduct}
          onBackPressed={() => this.props.handlePreviousClick()}
          onResetPressed={() => this.handleResetClicked()}
          onMakeContactRequestPressed={
            () => {
              const makeContactRequest = true;
              this.setState({ makeContactRequest });
            }}
        />
        <TechDataTable selectedProduct={this.props.selectedProduct} techdata={this.props.techData} />
      </Container >
    );
  }

  renderFormPage = () => {
    const Container = styled.div`
      display: flex;
      align-items: center;
      flex-flow: column;
      margin-bottom: 48px;
    `;

    const product = this.props.selectedProduct as unknown as IProduct;

    const submitForm = () => (
      values: {
        email: string,
        firstname: string,
        lastname: string,
        company: string,
        phone: string,
        country: string,
        marketsegment: string,
        mbquote: string
      },
      actions: FormikActions<{
        email: string,
        firstname: string,
        lastname: string,
        company: string,
        phone: string,
        country: string,
        marketsegment: string,
        mbquote: string
      }>) => {
      this.setState({ formInput: values });
      fetchJsonp(`https://pages.wartsila.digital/l/251562/2019-09-30/pxfwfs?${new URLSearchParams(values)}`, {
        jsonpCallbackFunction: 'callback'
      }).then((response) => {
        return response.json();
      }).then((json) => {
        if (json.result) {
          this.setFinished(true);
          actions.setSubmitting(false);
        } else {
          this.setError('Please fill in the required fields.');
        }
      }).catch(() => {
        this.setError('An error occurred.');
        actions.setSubmitting(false);
      });
    };

    let configuratorValue = '';
    if (product) {
      product.metadata.forEach((metadata) => {
        if (metadata.key !== 'engine-id') {
          configuratorValue += `${metadata.key}: ${metadata.value}${metadata.unit ? ' ' + metadata.unit : ''}; `;
        }
      });
    }
    return (
      <Container>
        <Title title="Contact Details" subtitle="Request a quotation" />
        <ContactForm
          formInput={this.state.formInput}
          error={this.state.error}
          configuratorValue={configuratorValue}
          onSubmit={submitForm()}
        />
        <NavigationContainer>
          <BackButton
            hidden={false}
            onClick={
              () => {
                const makeContactRequest = false;
                this.setState({ makeContactRequest });
              }}
            disabled={false}
          >
            Back
          </BackButton>
        </NavigationContainer>
      </Container >
    );
  }

  renderFinishedPage = () => {
    return (
      <ResultsContainer>
        <Title title="Done!" />
        <div>Thank you for your interest in Wärtsilä.</div>
        <div>Wärtsilä Customer Support will handle your enquiry and connect you with the right person.</div>
        <div>If you need to talk with the customer support,
           please call one of our <a href="https://pages.wartsila.digital/l/251562/rt-customer-support-centre-csc/rd8zz4"> 24h telephone numbers </a>
        </div>
      </ResultsContainer>
    );
  }

  renderConfigurationComponents = (showResults: boolean, isApplicationNull: boolean) => {
    return (
      <Container>
        {showResults && this.renderResults()}
        {!showResults && isApplicationNull && this.renderApplicationSelection(this.props.applications)}
        {!showResults && !isApplicationNull && <Step />}
        {this.state.finished && this.renderFinishedPage()}
      </Container>
    );
  }

  renderFinalizationComponents = (finished: boolean, makeContactRequest: boolean) => {
    return (
      <Container>
        {!finished && !makeContactRequest && this.renderTechDataPage()}
        {!finished && makeContactRequest && this.renderFormPage()}
        {finished && this.renderFinishedPage()}
      </Container>
    );
  }

  render() {
    const {
      application,
      showResults,
      selectedProduct } = this.props;

    if (!selectedProduct) {
      return this.renderConfigurationComponents(showResults, application === null);
    } else {
      return this.renderFinalizationComponents(this.state.finished, this.state.makeContactRequest);
    }
  }
}

const mapStateToProps = (state: IApplicationState) => {
  return {
    applications: getApplications(state),
    products: getProducts(state),
    application: state.appReducer.application,
    inputs: state.appReducer.inputs,
    selection: state.appReducer.selection,
    showResults: state.appReducer.showResults,
    productToBeSelected: state.appReducer.productToBeSelected,
    selectedProduct: state.appReducer.selectedProduct,
    filteredProducts: getFilteredProducts(state),
    techData: state.appReducer.techData
  };
};

const actions = {
  setApplication,
  setInput,
  handlePreviousClick,
  handleNextClick,
  handleResetClick,
  handleResultSelectedClick
};

export default connect(mapStateToProps, actions)(App);
